import { useState } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useOrganizationVideos } from '../../hooks/useOrganizationVideos';
import { VideosContext } from '../../contexts/VideosContext';
import { DashboardLayout } from './components/DashboardLayout';
import { DashboardNav } from './components/DashboardNav';
import { DashboardHeader } from './components/DashboardHeader';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { LogoutIcon } from '../../assets/LogoutIcon';
import { getSectionTitle } from './sectionTitle';
import './DashboardScreen.css';

// Sections available in the main dashboard (non-admin sections)
export type Section = 'players' | 'calls' | 'video' | 'notes' | 'scholarships' | 'schedule' | 'trustedContacts' | 'duplicates' | 'messaging' | 'admin' | 'crm' | 'settings';


export const DashboardScreen = () => {
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { user, logout } = useAuth();
  // Already memoized by the hook — filter state lives in there now, so an unstable value
  // would re-render every VideosContext consumer on each keystroke.
  const videosState = useOrganizationVideos();

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  return (
    <DashboardLayout>
      <DashboardNav
        onLogout={handleLogoutClick}
      />
      <div className="dashboard-content">
        <DashboardHeader
          title={getSectionTitle(location.pathname)}
          userEmail={user?.email || 'Unknown'}
        />
        <main className="dashboard-section-container" id="main-content" tabIndex={-1}>
          <ErrorBoundary
            fallbackTitle="Section Error"
            fallbackMessage="This section encountered an error. Try switching to another section or refreshing the page."
          >
            <VideosContext.Provider value={videosState}>
              <Outlet />
            </VideosContext.Provider>
          </ErrorBoundary>
        </main>
      </div>

      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes, Logout"
        cancelText="Cancel"
        icon={<LogoutIcon />}
      />
    </DashboardLayout>
  );
};
